/**
 * ShopifyAIEditor
 *
 * Workflow node editor for the "Shopify AI" action.
 *
 * Architecture:
 *   ① AI Config       — which LLM + system prompt
 *   ② Shopify Config  — which store (credentials)
 *   ③ MCP Servers     — tools the AI can call
 *   ④ Scenario Prompts — copy/paste snippets for the AI Config system prompt
 *   ⑤ Specialists     — optional delegation
 *
 * Shopify live tools (admin controller):
 *   - shopify_get_order          → GET /Shopify/Orders/{id}
 *   - shopify_get_recent_orders  → GET /Shopify/Orders/Recent (by email)
 *   - shopify_search_products    → GET /Shopify/Products/Search
 *   - shopify_get_customer       → GET /Shopify/Customers (by email)
 */
import * as React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useShopifyConfigs from "@/hooks/useShopifyConfigs";
import useMcpServers from "@/hooks/useMcpServers";
import AISetupGuide from "./AISetupGuide";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import AIContextInspector from "./AIContextInspector";
import McpStaleBadge from "./McpStaleBadge";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

const SHOPIFY_SCENARIOS = [
  {
    id: "order_status",
    label: "Order Status Lookup",
    description: "Customer wants to check the status of their order.",
    prompt: `When a customer asks about their order, use shopify_get_order or shopify_get_recent_orders with their email to find the order. Return status, items, total, tracking. If order is unfulfilled, give an honest timeline.`
  },
  {
    id: "product_search",
    label: "Product Search & Recommendations",
    description: "Customer asks about a product or wants recommendations.",
    prompt: `When a customer asks about a product or wants recommendations, use shopify_search_products with relevant keywords. Return the top 3 results with price, availability, and a brief description. If the customer mentions a specific product type, search for it directly.`
  },
  {
    id: "account_lookup",
    label: "Account & Purchase History",
    description: "Customer wants to know about their account or purchase history.",
    prompt: `When a customer wants to know about their account or purchase history, use shopify_get_customer with their email. Summarize their order history and account status. If they mention a specific order issue, use shopify_get_order for details.`
  },
  {
    id: "returns_refund",
    label: "Returns & Refund Inquiry",
    description: "Customer asks about a return or refund.",
    prompt: `When a customer asks about a return or refund, look up their order first. Confirm the order is within the return window. Explain the return process: [customize with your policy]. If eligible, guide them through the steps. Escalate if outside policy.`
  },
  {
    id: "cart_recovery",
    label: "Cart Recovery Support",
    description: "Customer mentions an abandoned cart or needs help completing a purchase.",
    prompt: `When a customer mentions an abandoned cart or asks why they didn't complete a purchase, acknowledge any concerns they might have (shipping cost, security, checkout difficulty). Offer to help them complete the purchase and ask if they have any questions about the products in their cart.`
  }
];

export default function ShopifyAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { items: shopifyConfigs, loading: shopifyLoading } = useShopifyConfigs(0);
  const { items: mcpServers } = useMcpServers();
  const [copiedScenario, setCopiedScenario] = useState<string | null>(null);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));
  const shopifyOptions = shopifyConfigs.map((s) => ({
    value: s.id,
    label: s.agencyName || `Config #${s.id}`
  }));

  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName =
    (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];
  const enabledToolNames = enabledMcpServers
    .filter((s) => selectedMcpIds.includes(s.id))
    .flatMap((s) => s.toolNames ?? []);

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  async function copyScenarioPrompt(id: string, prompt: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedScenario(id);
      setTimeout(() => setCopiedScenario(null), 2000);
    } catch { /* graceful fallback */ }
  }

  return (
    <>
      <AISetupGuide
        dataSourceName="Shopify"
        dataSourcePath="/integrations/shopify"
        hasAiConfig={!!selectedAiConfig}
        hasMcpServers={enabledMcpServers.length > 0}
        hasDataSourceConfig={!!node.shopifyConfigId}
      />

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found. <Link to="/bots" className="underline font-medium">Create one first →</Link>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            No system prompt yet. <Link to="/bots" className="underline">Add one →</Link> Use the Scenario Prompts below.
          </p>
        )}
      </FormGroup>

      {/* ── Step 2: Shopify Config ── */}
      <FormGroup label="Shopify Store">
        {!shopifyLoading && shopifyConfigs.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No Shopify store connected.{" "}
            <Link to="/integrations/shopify" className="underline font-medium">Connect a store →</Link>
            <p className="mt-1 text-slate-500">
              The Shopify config stores your store credentials so the AI can look up orders,
              customers, and products.
            </p>
          </div>
        ) : (
          <>
            <Select
              options={[
                { value: 0, label: "— Select Shopify Store —" },
                ...shopifyOptions
              ]}
              value={node.shopifyConfigId ?? 0}
              onChange={(value) => onUpdate({ shopifyConfigId: Number(value) || undefined })}
            />
          </>
        )}
      </FormGroup>

      {/* ── Step 3: MCP Tool Servers ── */}
      <FormGroup label="MCP Tool Servers">
        {enabledMcpServers.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            No MCP tool servers configured.{" "}
            <Link to="/integrations/mcp" className="text-blue-600 underline">Add an MCP server →</Link>
            <p className="mt-1">
              MCP servers give the AI tools:{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">shopify_get_order</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">shopify_get_recent_orders</code>,{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-xs">shopify_search_products</code>, etc.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-1">
            {providerUnsupported && (
              <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded border border-amber-200">
                MCP tools require Claude or OpenAI. The selected AI config may not support them.
              </p>
            )}
            {enabledMcpServers.map((server) => {
              const checked = selectedMcpIds.includes(server.id);
              return (
                <label
                  key={server.id}
                  className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                    checked ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 shrink-0"
                    checked={checked}
                    onChange={(e) => toggleMcpServer(server.id, e.target.checked)}
                    aria-label={`Enable MCP server: ${server.name}`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{server.name}</span>
                      {server.toolCount > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                          {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                        </span>
                      )}
                      <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                    </div>
                    {server.description && <span className="text-xs text-slate-500">{server.description}</span>}
                    {server.toolNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {server.toolNames.slice(0, 6).map((t) => (
                          <span key={t} className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{t}</span>
                        ))}
                        {server.toolNames.length > 6 && (
                          <span className="text-xs text-slate-400 px-1 py-0.5">+{server.toolNames.length - 6} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </FormGroup>

      {/* ── Scenario Prompts ── */}
      <div className="mt-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Scenario Prompts</span>
          <span className="text-xs text-slate-400 italic">for your AI Config</span>
        </div>
        <p className="text-xs text-slate-500 mb-2 leading-relaxed">
          Copy these into your AI Config's system prompt. Customize the bracketed sections for your store's specific policies.
        </p>
        <div className="flex flex-col gap-2">
          {SHOPIFY_SCENARIOS.map((scenario) => {
            const isExpanded = expandedScenario === scenario.id;
            const isCopied = copiedScenario === scenario.id;
            return (
              <div key={scenario.id} className="border border-slate-200 rounded bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedScenario(isExpanded ? null : scenario.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                  aria-expanded={isExpanded}
                >
                  <div>
                    <span className="text-xs font-medium text-slate-800">{scenario.label}</span>
                    {!isExpanded && <p className="text-xs text-slate-400 mt-0.5">{scenario.description}</p>}
                  </div>
                  <span className="text-slate-400 text-xs ml-2 shrink-0">{isExpanded ? "▲" : "▼"}</span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <p className="text-xs text-slate-500 mb-1.5">{scenario.description}</p>
                    <pre className="text-xs bg-slate-900 text-slate-100 rounded p-2.5 whitespace-pre-wrap leading-relaxed font-mono mb-2">
                      {scenario.prompt}
                    </pre>
                    <button
                      type="button"
                      onClick={() => copyScenarioPrompt(scenario.id, scenario.prompt)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        isCopied ? "border-green-300 bg-green-50 text-green-700" : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isCopied ? "✓ Copied!" : "Copy prompt snippet"}
                    </button>
                    <p className="text-xs text-slate-400 mt-1.5">Paste into your AI Config's system prompt, then customize the bracketed sections.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <AIContextInspector mcpToolNames={enabledToolNames} />

      <div className="mt-4 pt-3 border-t border-slate-200">
        <AISpecialistSection node={node} onUpdate={onUpdate} aiConfigurations={aiConfigurations} enabledMcpServers={enabledMcpServers} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
